<template>
    <AxRoadPage :title="$t('ax_road.search')" class="radial-search-view">
        <div class="radial-search-view__date-time l-stack l-gap-2 u-bb">
            <div class="l-full-width l-gap-2 l-inline">
                <DateInput
                    v-model="dateRange"
                    class="radial-search-view__daterange l-full-width"
                    :placeholder="$t('startDate') + ' - ' + $t('endDate')"
                    :disabled-dates="{ from: today }"
                    :max-range="2"
                    range
                />
            </div>
            <div class="l-full-width l-gap-2 l-inline">
                <TimeInput
                    v-model="startTime"
                    :placeholder="$t('startTime')"
                    :max-time="endTime"
                    manual-input
                    hide-dropdown
                />
                <TimeInput
                    v-model="endTime"
                    :placeholder="$t('endTime')"
                    :min-time="startTime"
                    manual-input
                    hide-dropdown
                />
            </div>
            <div
                class="l-full-width l-inline l-gap-1 radial-search-view__quick-dates"
            >
                <BaseButton
                    variant="outline"
                    size="small"
                    @click="setDaysAgo(0)"
                >
                    {{ $t('today') }}</BaseButton
                >
                <BaseButton
                    variant="outline"
                    size="small"
                    @click="setDaysAgo(1)"
                >
                    {{ $t('yesterday') }}</BaseButton
                >
                <BaseButton
                    variant="outline"
                    size="small"
                    @click="setDaysAgo(2)"
                >
                    {{ $t('twoDaysAgo') }}</BaseButton
                >
            </div>
        </div>
        <div class="radial-search-view__search l-stack l-gap-1">
            <div class="l-inline">
                <SegmentButton
                    v-model="selectedSearchType"
                    class="radial-search-view__type-switch"
                    :options="searchTypes"
                    small
                />
            </div>
            <div
                v-if="selectedSearchType[0] === 'address'"
                class="l-stack l-gap-2"
            >
                <AddressSearch
                    v-model="selectedAddress"
                    class="radial-search-view__address-search"
                    :default-address="selectedAddress"
                    :placeholder="$t('addressPlaceholder')"
                    @input="setCoordinates"
                />
                <div class="l-inline l-gap-1 l-spread">
                    <div class="">{{ $t('radius') }}</div>
                    <div class="">
                        {{ sliderTooltipFormatter(searchRadius) }}
                    </div>
                </div>
                <VSlider
                    v-model="searchRadius"
                    adsorb
                    drag-on-click
                    tooltip="none"
                    :data="radiusOptions"
                    @change="handleSearchRadiusChange"
                    @drag="handleSearchRadiusChange"
                />
            </div>
            <div v-else-if="selectedSearchType[0] === 'zone'">
                <BaseMultiselect
                    v-model="selectedZone"
                    :options="locationsSortedByName"
                    track-by="id"
                    label="name"
                />
            </div>
            <div class="radial-search-view__asset-types l-full-width">
                <BaseMultiselect
                    :value="shownAssetTypes"
                    :options="assetTypesPrepared"
                    track-by="id"
                    label="name"
                    :placeholder="$t('assetTypes')"
                    multiple
                    clearable
                    @input="handleAssetTypesChange"
                />
            </div>
            <div>
                <BaseButton
                    class="radial-search-view__search-button"
                    variant="solid"
                    size="small"
                    :disabled="isSearchDisabled"
                    @click="handleSearch"
                >
                    {{ $t('search') }}
                </BaseButton>
            </div>
        </div>
    </AxRoadPage>
</template>

<script>
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex'
import moment from 'moment'
import VSlider from 'vue-slider-component'

import AddressSearch from '@/components/AddressSearch'
import AxRoadPage from '@/components/ax-road/AxRoadPage'
import BaseButton from '@/components/redesigned/BaseButton'
import BaseMultiselect from '@/components/redesigned/BaseMultiselect'
import DateInput from '@/components/redesigned/DateInput'
import SegmentButton from '@/components/redesigned/SegmentButton'
import TimeInput from '@/components/redesigned/TimeInput'

const radiusOptions = [
    ...Array.from({ length: 11 }, (_, i) => i * 50),
    ...Array.from({ length: 5 }, (_, i) => 500 + i * 100),
    ...Array.from({ length: 18 }, (_, i) => 1000 + i * 500),
    ...Array.from({ length: 41 }, (_, i) => 10000 + i * 1000),
]

export default {
    name: 'RadialSearchView',
    components: {
        AddressSearch,
        AxRoadPage,
        BaseButton,
        BaseMultiselect,
        DateInput,
        SegmentButton,
        TimeInput,
        VSlider,
    },
    data() {
        return {
            dateRange: [],
            startTime: '00:00',
            endTime: '23:59',
            addressCoordinates: null,
            selectedAddress: null,
            searchTypes: [
                { value: 'address', label: this.$t('searchByAddress') },
                { value: 'zone', label: this.$t('searchByZone') },
            ],
            selectedSearchType: ['address'],
            searchRadius: 100,
            radiusOptions: radiusOptions,
            selectedZone: null,
            initializingFromQuery: false,
            twoDays: 2 * 24 * 60 * 60 * 1000,
        }
    },
    computed: {
        ...mapGetters('location', ['locationsSortedByName', 'locationsById']),
        ...mapState('axroad', [
            'radialSearchStart',
            'radialSearchEnd',
            'radialSearchRadius',
            'radialSearchZone',
            'radialSearchCenter',
            'radialSearchAddress',
            'radialSearchAssetTypes',
            'radialSearchMode',
        ]),
        ...mapState('tracker', ['assetTypes']),
        ...mapGetters('tracker', ['assetTypesById']),
        assetTypesPrepared() {
            return [...this.assetTypes].sort((a, b) =>
                a.name.localeCompare(b.name)
            )
        },
        today() {
            return moment()
                .endOf('day')
                .toDate()
        },
        isSearchDisabled() {
            return (
                !this.startDate ||
                !this.endDate ||
                !this.startTime ||
                !this.endTime ||
                (this.selectedSearchType[0] === 'address' &&
                    !this.selectedAddress) ||
                (this.selectedSearchType[0] === 'zone' && !this.selectedZone)
            )
        },
        startDate: {
            get() {
                return this.dateRange[0]
            },
            set(val) {
                this.dateRange = [val]
            },
        },
        endDate: {
            get() {
                return this.dateRange[1]
            },
            set(val) {
                this.dateRange = [this.dateRange[0], val]
            },
        },
        shownAssetTypes() {
            return (
                this.radialSearchAssetTypes?.map(
                    id => this.assetTypesById[id]
                ) || []
            )
        },
    },
    watch: {
        selectedZone(newZone) {
            if (newZone && this.selectedSearchType[0] === 'zone') {
                this.setActiveLocationOnMap(newZone)
                this.setRadialSearchZone(newZone)
            }
        },
        selectedAddress() {
            if (
                this.selectedAddress?.latitude &&
                this.selectedAddress?.longitude
            ) {
                this.setCoordinates(this.selectedAddress)
                this.setRadialSearchAddress(this.selectedAddress)
                this.setAddressCoordinates({
                    lat: this.selectedAddress.latitude,
                    lng: this.selectedAddress.longitude,
                })
            } else {
                this.setRadialSearchAddress(null)
                this.setAddressCoordinates(null)
            }
        },
        selectedSearchType([newType]) {
            this.setRadialSearchMode(newType)
            if (newType === 'zone') {
                this.setActiveLocationOnMap(this.selectedZone)
            } else {
                this.setActiveLocationOnMap(null)
                this.setRadialSearchCenter(this.addressCoordinates)
            }
        },
        radialSearchAddress(address) {
            if (address !== this.selectedAddress) {
                this.selectedAddress = address
            }
        },
    },
    created() {
        if (this.radialSearchStart) {
            const start = moment(this.radialSearchStart)
            this.startDate = start.toDate()
            this.startTime = start.format('HH:mm')
        }
        if (this.radialSearchEnd) {
            const end = moment(this.radialSearchEnd)
            this.endDate = end.toDate()
            this.endTime = end.format('HH:mm')
        }
        if (this.radialSearchAddress) {
            this.selectedAddress = this.radialSearchAddress
            this.setCoordinates(this.selectedAddress)
        }
        if (this.radialSearchRadius) {
            this.searchRadius = this.radialSearchRadius
        }
        if (this.radialSearchZone) {
            this.selectedZone = this.radialSearchZone
        }
        if (this.radialSearchMode) {
            this.selectedSearchType = [this.radialSearchMode]
        }
    },
    methods: {
        ...mapMutations('axroad', [
            'setRadialSearchCenter',
            'setRadialSearchRadius',
            'setRadialSearchMode',
            'setRadialSearchStart',
            'setRadialSearchEnd',
            'setRadialSearchZone',
            'setRadialSearchAddress',
            'setRadialSearchAssetTypes',
        ]),
        ...mapMutations('location', ['setActiveLocationOnMap']),
        ...mapMutations('map', ['setForceAlign', 'setAddressCoordinates']),
        ...mapActions('axroad', ['fetchRadialSearchResults']),
        setDaysAgo(numDays) {
            const date = new Date()
            date.setDate(date.getDate() - numDays)
            this.startDate = date
            this.endDate = date
            this.startTime = '00:00'
            this.endTime = '23:59'
        },
        setCoordinates(addressObj) {
            if (!addressObj?.latitude || !addressObj?.longitude) {
                return
            }
            const { latitude, longitude } = addressObj
            this.addressCoordinates = { lat: latitude, lng: longitude }
            this.setRadialSearchCenter(this.addressCoordinates)
        },
        handleSearchRadiusChange(radius) {
            this.setRadialSearchRadius(radius)
        },
        handleAssetTypesChange(assetTypes) {
            const uniqueAssetTypes =
                [...new Set(assetTypes)].filter(Boolean) || []
            this.setRadialSearchAssetTypes(uniqueAssetTypes.map(({ id }) => id))
        },
        handleSearch() {
            if (this.isSearchDisabled) {
                return
            }
            const start =
                this.startDate && this.startTime
                    ? moment(this.startDate)
                          .set({
                              hour: this.startTime.split(':')[0],
                              minute: this.startTime.split(':')[1],
                          })
                          .toISOString()
                    : null
            const end =
                this.endDate && this.endTime
                    ? moment(this.endDate)
                          .set({
                              hour: this.endTime.split(':')[0],
                              minute: this.endTime.split(':')[1],
                          })
                          .toISOString()
                    : null
            this.setRadialSearchStart(start)
            this.setRadialSearchEnd(end)
            this.fetchRadialSearchResults()
            this.$router.push({
                name: 'axRoadSearchResults',
            })
        },
        sliderTooltipFormatter(value) {
            return value < 1000 ? `${value} m` : `${value / 1000} km`
        },
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.setForceAlign(true)
        })
    },
    beforeRouteLeave(to, from, next) {
        if (!to.path.match(/\/map\/ax-road\/search/)) {
            this.setForceAlign(false)
            this.setActiveLocationOnMap(null)
            this.setAddressCoordinates(null)
        }
        next()
    },
}
</script>

<style lang="scss" scoped>
.radial-search-view {
    &__date-time {
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid $color-gray-light;
    }
    &__search {
        gap: 12px;
    }
    &__type-switch {
        border: 1px solid currentColor;
    }

    ::v-deep .vdp-datepicker__calendar {
        z-index: 200;
        position: fixed !important;
    }

    ::v-deep .base-button {
        padding: 6px 12px;
        border-radius: 8px;
        font-weight: bolder;
        font-size: $fs-base;
        border-color: currentColor;
    }
}
</style>

<i18n>
{
    "en": {
        "startDate": "Start Date",
        "endDate": "End Date",
        "startTime": "Start Time",
        "endTime": "End Time",
        "today": "Today",
        "yesterday": "Yesterday",
        "twoDaysAgo": "Two Days Ago",
        "searchByAddress": "By Address",
        "addressPlaceholder": "Search address or select on map",
        "radius": "Radius",
        "searchByZone": "By Zone",
        "search": "Search",
        "searchByPointOnMap": "Point on Map",
        "assetTypes": "Asset Types"
    },
    "de": {
        "startDate": "Startdatum",
        "endDate": "Enddatum",
        "startTime": "Startzeit",
        "endTime": "Endzeit",
        "today": "Heute",
        "yesterday": "Gestern",
        "twoDaysAgo": "Vorgestern",
        "searchByAddress": "Nach Adresse",
        "addressPlaceholder": "Adresse suchen oder auf Karte auswählen",
        "radius": "Radius",
        "searchByZone": "Nach Zone",
        "search": "Suchen",
        "searchByPointOnMap": "Punkt auf der Karte",
        "assetTypes": "Asset-Typen"
    },
    "fr": {
        "startDate": "Date de début",
        "endDate": "Date de fin",
        "startTime": "Heure de début",
        "endTime": "Heure de fin",
        "today": "Aujourd'hui",
        "yesterday": "Hier",
        "twoDaysAgo": "Avant-hier",
        "searchByAddress": "Par adresse",
        "addressPlaceholder": "Rechercher une adresse ou sélectionner sur la carte",
        "radius": "Rayon",
        "searchByZone": "Par zone",
        "search": "Rechercher",
        "searchByPointOnMap": "Point sur la carte",
        "assetTypes": "Types d'actifs"
    },
    "it": {
        "startDate": "Data di inizio",
        "endDate": "Data di fine",
        "startTime": "Ora di inizio",
        "endTime": "Ora di fine",
        "today": "Oggi",
        "yesterday": "Ieri",
        "twoDaysAgo": "Due giorni fa",
        "searchByAddress": "Per indirizzo",
        "addressPlaceholder": "Cerca un indirizzo o seleziona sulla mappa",
        "radius": "Raggio",
        "searchByZone": "Per zona",
        "search": "Cerca",
        "searchByPointOnMap": "Punto sulla mappa",
        "assetTypes": "Tipi di asset"
    }
}
</i18n>
